<template>
  <div class="upload-zone-component">
    <div class="box">
      <div class="level">
        <div class="level-left">
          <div class="level-item">
            <h4 class="title is-4">{{ $t("archive.file_upload_zone") }}</h4>
          </div>
        </div>
        <div class="level-right">
          <div class="level-item">
            <div class="buttons">
              <button class="button is-info" @click="toggleBulkMode">
                <span class="icon">
                  <i class="fas" :class="bulkMode ? 'fa-file' : 'fa-files'"></i>
                </span>
                <span>{{
                  bulkMode ? $t("archive.single_mode") : $t("archive.bulk_mode")
                }}</span>
              </button>
              <button
                class="button is-success"
                @click="clearAll"
                v-if="uploadQueue.length > 0"
              >
                <span class="icon">
                  <i class="fas fa-broom"></i>
                </span>
                <span>{{ $t("archive.clear_all") }}</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Upload Configuration -->
      <div class="upload-config" v-if="!bulkMode">
        <div class="columns">
          <div class="column is-6">
            <div class="field">
              <label class="label">{{ $t("archive.customer") }} *</label>
              <div class="control">
                <div class="select is-fullwidth">
                  <select
                    v-model="defaultConfig.customer_id"
                    @change="onCustomerChange"
                  >
                    <option value="">
                      {{ $t("archive.select_customer") }}
                    </option>
                    <option
                      v-for="customer in customers"
                      :key="customer.id"
                      :value="customer.id"
                    >
                      {{ customer.name }}
                    </option>
                  </select>
                </div>
              </div>
            </div>
          </div>

          <div class="column is-6">
            <div class="field">
              <label class="label">{{ $t("archive.expedient") }} *</label>
              <div class="control">
                <div class="select is-fullwidth">
                  <select
                    v-model="defaultConfig.expedient_id"
                    :disabled="!defaultConfig.customer_id"
                  >
                    <option value="">
                      {{ $t("archive.select_expedient") }}
                    </option>
                    <option
                      v-for="expedient in filteredExpedients"
                      :key="expedient.id"
                      :value="expedient.id"
                    >
                      {{ expedient.name }}
                    </option>
                  </select>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="columns">
          <div class="column is-6">
            <div class="field">
              <label class="label">{{ $t("archive.category") }}</label>
              <div class="control">
                <div class="select is-fullwidth">
                  <select v-model="defaultConfig.category_id">
                    <option value="">
                      {{ $t("archive.select_category") }}
                    </option>
                    <option
                      v-for="category in categories"
                      :key="category.id"
                      :value="category.id"
                    >
                      {{ category.name }}
                    </option>
                  </select>
                </div>
              </div>
            </div>
          </div>

          <div class="column is-6">
            <div class="field">
              <label class="label">{{ $t("archive.tags") }}</label>
              <div class="control">
                <input
                  class="input"
                  type="text"
                  v-model="defaultConfig.tags"
                  :placeholder="$t('archive.tags_placeholder')"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Drop Zone -->
      <div
        class="drop-zone"
        :class="{
          'is-dragover': isDragOver,
          'is-disabled':
            !bulkMode &&
            (!defaultConfig.customer_id || !defaultConfig.expedient_id),
        }"
        @dragover.prevent="handleDragOver"
        @dragleave.prevent="handleDragLeave"
        @drop.prevent="handleDrop"
      >
        <div class="drop-zone-content">
          <div class="drop-zone-icon">
            <i class="fas fa-cloud-upload-alt fa-4x"></i>
          </div>

          <h5 class="title is-5">{{ $t("archive.drop_files_here") }}</h5>
          <p class="subtitle">{{ $t("archive.or_click_to_browse") }}</p>

          <input
            type="file"
            ref="fileInput"
            multiple
            @change="handleFileSelect"
            accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.jpg,.jpeg,.png,.gif,.txt,.zip,.rar"
            style="display: none"
          />

          <button
            class="button is-primary is-large"
            @click="$refs.fileInput.click()"
            :disabled="
              !bulkMode &&
              (!defaultConfig.customer_id || !defaultConfig.expedient_id)
            "
          >
            <span class="icon">
              <i class="fas fa-folder-open"></i>
            </span>
            <span>{{ $t("archive.browse_files") }}</span>
          </button>

          <p class="help">
            {{ $t("archive.supported_formats") }}: PDF, DOC, XLS, PPT, Images,
            TXT, ZIP
          </p>
        </div>
      </div>

      <!-- Upload Queue -->
      <div v-if="uploadQueue.length > 0" class="upload-queue">
        <div class="level">
          <div class="level-left">
            <div class="level-item">
              <h5 class="title is-5">
                {{ $t("archive.upload_queue") }} ({{ uploadQueue.length }})
              </h5>
            </div>
          </div>
          <div class="level-right">
            <div class="level-item">
              <div class="buttons">
                <button
                  class="button is-primary"
                  @click="uploadAll"
                  :class="{ 'is-loading': isUploading }"
                  :disabled="isUploading || !canUploadAll"
                >
                  <span class="icon">
                    <i class="fas fa-upload"></i>
                  </span>
                  <span>{{ $t("archive.upload_all") }}</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        <div class="queue-list">
          <div
            v-for="(item, index) in uploadQueue"
            :key="index"
            class="queue-item"
            :class="{
              'is-uploading': item.status === 'uploading',
              'is-success': item.status === 'success',
              'is-error': item.status === 'error',
            }"
          >
            <div class="queue-item-content">
              <div class="queue-file-info">
                <div class="file-icon">
                  <i :class="getFileIconFromName(item.file.name)"></i>
                </div>
                <div class="file-details">
                  <p class="file-name">{{ item.file.name }}</p>
                  <p class="file-meta">
                    {{ formatFileSize(item.file.size) }}
                    <span
                      v-if="item.config.customer_name"
                      class="customer-info"
                    >
                      → {{ item.config.customer_name }}
                      <span v-if="item.config.expedient_name"
                        >/ {{ item.config.expedient_name }}</span
                      >
                    </span>
                  </p>
                </div>
              </div>

              <div
                class="queue-status"
                v-if="bulkMode && !item.config.customer_id"
              >
                <div class="field has-addons">
                  <div class="control">
                    <div class="select is-small">
                      <select
                        v-model="item.config.customer_id"
                        @change="updateItemExpedients(item)"
                      >
                        <option value="">
                          {{ $t("archive.select_customer") }}
                        </option>
                        <option
                          v-for="customer in customers"
                          :key="customer.id"
                          :value="customer.id"
                        >
                          {{ customer.name }}
                        </option>
                      </select>
                    </div>
                  </div>
                  <div class="control">
                    <div class="select is-small">
                      <select
                        v-model="item.config.expedient_id"
                        :disabled="!item.config.customer_id"
                      >
                        <option value="">
                          {{ $t("archive.select_expedient") }}
                        </option>
                        <option
                          v-for="expedient in getFilteredExpedientsForItem(
                            item
                          )"
                          :key="expedient.id"
                          :value="expedient.id"
                        >
                          {{ expedient.name }}
                        </option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>

              <div class="queue-actions">
                <div v-if="item.status === 'pending'" class="buttons are-small">
                  <button
                    class="button is-primary"
                    @click="uploadSingle(item)"
                    :disabled="
                      !item.config.customer_id || !item.config.expedient_id
                    "
                  >
                    <span class="icon">
                      <i class="fas fa-upload"></i>
                    </span>
                  </button>
                  <button
                    class="button is-danger"
                    @click="removeFromQueue(index)"
                  >
                    <span class="icon">
                      <i class="fas fa-times"></i>
                    </span>
                  </button>
                </div>

                <div
                  v-else-if="item.status === 'uploading'"
                  class="upload-progress"
                >
                  <progress
                    class="progress is-primary is-small"
                    :value="item.progress"
                    max="100"
                  >
                    {{ item.progress }}%
                  </progress>
                </div>

                <div
                  v-else-if="item.status === 'success'"
                  class="upload-success"
                >
                  <span class="icon has-text-success">
                    <i class="fas fa-check-circle"></i>
                  </span>
                  <button
                    class="button is-small"
                    @click="removeFromQueue(index)"
                  >
                    <span class="icon">
                      <i class="fas fa-times"></i>
                    </span>
                  </button>
                </div>

                <div v-else-if="item.status === 'error'" class="upload-error">
                  <span class="icon has-text-danger" :title="item.error">
                    <i class="fas fa-exclamation-triangle"></i>
                  </span>
                  <button
                    class="button is-small is-danger"
                    @click="uploadSingle(item)"
                  >
                    <span class="icon">
                      <i class="fas fa-redo"></i>
                    </span>
                  </button>
                  <button
                    class="button is-small"
                    @click="removeFromQueue(index)"
                  >
                    <span class="icon">
                      <i class="fas fa-times"></i>
                    </span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Upload Statistics -->
      <div v-if="uploadStats.total > 0" class="upload-stats">
        <div class="level">
          <div class="level-item has-text-centered">
            <div>
              <p class="heading">{{ $t("archive.total") }}</p>
              <p class="title">{{ uploadStats.total }}</p>
            </div>
          </div>
          <div class="level-item has-text-centered">
            <div>
              <p class="heading">{{ $t("archive.uploaded") }}</p>
              <p class="title has-text-success">{{ uploadStats.success }}</p>
            </div>
          </div>
          <div class="level-item has-text-centered">
            <div>
              <p class="heading">{{ $t("archive.pending") }}</p>
              <p class="title has-text-warning">{{ uploadStats.pending }}</p>
            </div>
          </div>
          <div class="level-item has-text-centered">
            <div>
              <p class="heading">{{ $t("archive.errors") }}</p>
              <p class="title has-text-danger">{{ uploadStats.errors }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "UploadZone",
  data() {
    return {
      customers: [],
      expedients: [],
      categories: [],
      uploadQueue: [],
      isDragOver: false,
      isUploading: false,
      bulkMode: false,
      defaultConfig: {
        customer_id: "",
        expedient_id: "",
        category_id: "",
        tags: "",
      },
    };
  },
  computed: {
    filteredExpedients() {
      if (!this.defaultConfig.customer_id) return [];
      return this.expedients.filter(
        (exp) => exp.customer_id === this.defaultConfig.customer_id
      );
    },

    canUploadAll() {
      return this.uploadQueue.every(
        (item) => item.config.customer_id && item.config.expedient_id
      );
    },

    uploadStats() {
      const stats = {
        total: this.uploadQueue.length,
        success: 0,
        pending: 0,
        errors: 0,
      };

      this.uploadQueue.forEach((item) => {
        if (item.status === "success") stats.success++;
        else if (item.status === "error") stats.errors++;
        else stats.pending++;
      });

      return stats;
    },
  },
  mounted() {
    this.loadData();
  },
  methods: {
    async loadData() {
      try {
        // Load customers
        const customersResponse = await this.$api.get(
          "/api/v1/archive/customer/catalog"
        );
        this.customers = customersResponse.data;

        // Load expedients
        const expedientsResponse = await this.$api.get(
          "/api/v1/archive/expedient/catalog"
        );
        this.expedients = expedientsResponse.data;

        // Load categories
        const categoriesResponse = await this.$api.get(
          "/api/v1/archive/category/catalog"
        );
        this.categories = categoriesResponse.data;
      } catch (error) {
        console.error("Error loading data:", error);
        this.$toast.error(this.$t("archive.error_loading_data"));
      }
    },

    toggleBulkMode() {
      this.bulkMode = !this.bulkMode;
      if (!this.bulkMode) {
        // Clear queue when switching to single mode
        this.uploadQueue = [];
      }
    },

    onCustomerChange() {
      this.defaultConfig.expedient_id = "";
    },

    handleDragOver(event) {
      if (
        !this.bulkMode &&
        (!this.defaultConfig.customer_id || !this.defaultConfig.expedient_id)
      ) {
        event.dataTransfer.dropEffect = "none";
        return;
      }
      this.isDragOver = true;
      event.dataTransfer.dropEffect = "copy";
    },

    handleDragLeave() {
      this.isDragOver = false;
    },

    handleDrop(event) {
      this.isDragOver = false;
      if (
        !this.bulkMode &&
        (!this.defaultConfig.customer_id || !this.defaultConfig.expedient_id)
      ) {
        return;
      }

      const files = Array.from(event.dataTransfer.files);
      this.addFilesToQueue(files);
    },

    handleFileSelect(event) {
      const files = Array.from(event.target.files);
      this.addFilesToQueue(files);
      event.target.value = ""; // Reset input
    },

    addFilesToQueue(files) {
      files.forEach((file) => {
        if (this.isValidFile(file)) {
          const config = this.bulkMode
            ? {
                customer_id: "",
                expedient_id: "",
                category_id: "",
                tags: "",
              }
            : { ...this.defaultConfig };

          // Add customer and expedient names for display
          if (config.customer_id) {
            const customer = this.customers.find(
              (c) => c.id === config.customer_id
            );
            config.customer_name = customer ? customer.name : "";
          }

          if (config.expedient_id) {
            const expedient = this.expedients.find(
              (e) => e.id === config.expedient_id
            );
            config.expedient_name = expedient ? expedient.name : "";
          }

          this.uploadQueue.push({
            file: file,
            config: config,
            status: "pending",
            progress: 0,
            error: null,
          });
        } else {
          this.$toast.error(
            this.$t("archive.invalid_file_type", { name: file.name })
          );
        }
      });
    },

    isValidFile(file) {
      const allowedTypes = [
        "application/pdf",
        "application/msword",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        "application/vnd.ms-excel",
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        "application/vnd.ms-powerpoint",
        "application/vnd.openxmlformats-officedocument.presentationml.presentation",
        "image/jpeg",
        "image/png",
        "image/gif",
        "text/plain",
        "application/zip",
        "application/x-rar-compressed",
      ];

      return (
        allowedTypes.includes(file.type) ||
        file.name.match(
          /\.(pdf|doc|docx|xls|xlsx|ppt|pptx|jpg|jpeg|png|gif|txt|zip|rar)$/i
        )
      );
    },

    updateItemExpedients(item) {
      item.config.expedient_id = "";
      if (item.config.customer_id) {
        const customer = this.customers.find(
          (c) => c.id === item.config.customer_id
        );
        item.config.customer_name = customer ? customer.name : "";
      }
    },

    getFilteredExpedientsForItem(item) {
      if (!item.config.customer_id) return [];
      return this.expedients.filter(
        (exp) => exp.customer_id === item.config.customer_id
      );
    },

    async uploadAll() {
      this.isUploading = true;

      for (const item of this.uploadQueue) {
        if (item.status === "pending") {
          await this.uploadSingle(item);
        }
      }

      this.isUploading = false;
      this.$emit("upload-completed", this.uploadStats);
    },

    async uploadSingle(item) {
      if (!item.config.customer_id || !item.config.expedient_id) {
        this.$toast.error(this.$t("archive.select_customer_and_expedient"));
        return;
      }

      item.status = "uploading";
      item.progress = 0;

      try {
        const formData = new FormData();
        formData.append("file", item.file);
        formData.append("customer_id", item.config.customer_id);
        formData.append("expedient_id", item.config.expedient_id);
        if (item.config.category_id)
          formData.append("category_id", item.config.category_id);
        if (item.config.tags) formData.append("tags", item.config.tags);

        await this.$api.post("/api/v1/archive/file/upload", formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
          onUploadProgress: (progressEvent) => {
            item.progress = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
          },
        });

        item.status = "success";
        item.progress = 100;
        this.$emit("file-uploaded", item);
      } catch (error) {
        console.error("Error uploading file:", error);
        item.status = "error";
        item.error = error.response?.data?.message || error.message;
        item.progress = 0;
      }
    },

    removeFromQueue(index) {
      this.uploadQueue.splice(index, 1);
    },

    clearAll() {
      this.uploadQueue = [];
    },

    getFileIconFromName(filename) {
      const extension = filename.split(".").pop().toLowerCase();
      const iconMap = {
        pdf: "fas fa-file-pdf has-text-danger",
        doc: "fas fa-file-word has-text-info",
        docx: "fas fa-file-word has-text-info",
        xls: "fas fa-file-excel has-text-success",
        xlsx: "fas fa-file-excel has-text-success",
        ppt: "fas fa-file-powerpoint has-text-warning",
        pptx: "fas fa-file-powerpoint has-text-warning",
        jpg: "fas fa-file-image has-text-primary",
        jpeg: "fas fa-file-image has-text-primary",
        png: "fas fa-file-image has-text-primary",
        gif: "fas fa-file-image has-text-primary",
        txt: "fas fa-file-alt has-text-grey",
        zip: "fas fa-file-archive has-text-dark",
        rar: "fas fa-file-archive has-text-dark",
      };

      return iconMap[extension] || "fas fa-file has-text-grey";
    },

    formatFileSize(bytes) {
      if (!bytes) return "0 B";

      const k = 1024;
      const sizes = ["B", "KB", "MB", "GB"];
      const i = Math.floor(Math.log(bytes) / Math.log(k));

      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
    },
  },
};
</script>

<style scoped>
.upload-zone-component {
  padding: 20px;
}

.upload-config {
  margin-bottom: 2rem;
  padding: 1rem;
  background-color: #f8f9fa;
  border-radius: 6px;
}

.drop-zone {
  border: 3px dashed #dbdbdb;
  border-radius: 12px;
  padding: 3rem;
  text-align: center;
  margin: 2rem 0;
  transition: all 0.3s ease;
  cursor: pointer;
}

.drop-zone:hover:not(.is-disabled) {
  border-color: #3273dc;
  background-color: #f8f9fa;
}

.drop-zone.is-dragover {
  border-color: #3273dc;
  background-color: #e8f4fd;
  transform: scale(1.02);
}

.drop-zone.is-disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.drop-zone-content {
  pointer-events: none;
}

.drop-zone-icon {
  color: #3273dc;
  margin-bottom: 1rem;
}

.upload-queue {
  margin-top: 2rem;
}

.queue-list {
  max-height: 400px;
  overflow-y: auto;
  border: 1px solid #dbdbdb;
  border-radius: 6px;
}

.queue-item {
  border-bottom: 1px solid #f0f0f0;
  padding: 1rem;
  transition: background-color 0.2s ease;
}

.queue-item:last-child {
  border-bottom: none;
}

.queue-item.is-uploading {
  background-color: #fffbeb;
}

.queue-item.is-success {
  background-color: #f0fff4;
}

.queue-item.is-error {
  background-color: #fef2f2;
}

.queue-item-content {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.queue-file-info {
  display: flex;
  align-items: center;
  flex: 1;
}

.file-icon {
  width: 30px;
  text-align: center;
  margin-right: 10px;
}

.file-details {
  flex: 1;
}

.file-name {
  font-weight: 500;
  margin-bottom: 4px;
}

.file-meta {
  font-size: 0.875rem;
  color: #666;
}

.customer-info {
  margin-left: 10px;
  padding: 2px 6px;
  background-color: #e8f4fd;
  border-radius: 3px;
  font-size: 0.75rem;
}

.queue-status {
  margin-right: 1rem;
}

.queue-actions {
  min-width: 120px;
  text-align: right;
}

.upload-progress {
  width: 100px;
}

.upload-stats {
  margin-top: 2rem;
  padding: 1rem;
  background-color: #f8f9fa;
  border-radius: 6px;
}
</style>
